/*
 * Approvals — role-adaptive hub surfacing the queues each role has to act on.
 *
 * • Engineer / CS Supervisor / Manager / Admin: material requests to decide,
 *   completed service reports to sign-off, complaints ready to close.
 * • Storekeeper: materials approved by engineer (ready to issue),
 *   returnable items pending collection, view-only queue of pending approvals.
 * • Technician: my in-flight material requests (status of each MRQ).
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Check, Package, RotateCcw, X, Clock, Wrench, FileBarChart, Loader2, ClipboardCheck, Eye, ShoppingCart, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const APPROVE_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR', 'ENGINEER'];
const ISSUE_ROLES = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'STOREKEEPER'];

export default function Approvals() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [pendingReturns, setPendingReturns] = useState([]);
  const [pendingInspectionCosts, setPendingInspectionCosts] = useState([]);
  const [pendingPOs, setPendingPOs] = useState([]);
  const [pendingVouchers, setPendingVouchers] = useState([]);
  const [pendingNotes, setPendingNotes] = useState([]);
  const [pendingMRs, setPendingMRs] = useState([]);
  const [pendingPRs, setPendingPRs] = useState([]);
  const [pendingSRs, setPendingSRs] = useState([]);
  const [expandedPr, setExpandedPr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // Feb-2026 UX fix — engineer can peek at the linked complaint (title,
  // description, priority, reporter contact) inline before deciding on a PR.
  const togglePrExpand = (pr) => {
    setExpandedPr(expandedPr === pr.id ? null : pr.id);
  };

  const load = async () => {
    try {
      const [r1, r2, r3, r4, r5, r6, r7, r8, r9, r10] = await Promise.all([
        axios.get(`${API}/service-reports`, { withCredentials: true }),
        axios.get(`${API}/complaints`, { withCredentials: true }),
        axios.get(`${API}/materials/pending-returns`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/cost-approvals/pending`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/purchase-orders-pending-approval`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/vouchers`, { params: { status: 'pending_approval' }, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/credit-debit-notes-pending`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/material-requests`, { params: { status: 'pending' }, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/purchase-requests`, { params: { status: 'pending_engineer_approval' }, withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/service-reports`, { params: { status: 'completed' }, withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setReports(r1.data);
      setComplaints(r2.data);
      setPendingReturns(Array.isArray(r3.data) ? r3.data : (r3.data?.items || []));
      setPendingInspectionCosts(Array.isArray(r4.data) ? r4.data : []);
      setPendingPOs(Array.isArray(r5.data) ? r5.data : []);
      setPendingVouchers(Array.isArray(r6.data) ? r6.data : []);
      setPendingNotes(Array.isArray(r7?.data) ? r7.data : []);
      setPendingMRs(Array.isArray(r8.data) ? r8.data : []);
      setPendingPRs(Array.isArray(r9.data) ? r9.data : []);
      setPendingSRs(Array.isArray(r10.data) ? r10.data.filter(sr => sr.status === 'completed') : []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const doAction = async (verb, id, payload = {}) => {
    setBusy(`${verb}-${id}`);
    try {
      await axios.put(`${API}/service-reports/${id}/${verb}`, payload, { withCredentials: true });
      toast.success(verb.replace(/-/g, ' ') + ' ✓');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const canApprove = APPROVE_ROLES.includes(user?.role);
  const canApproveInspectionCost = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER'].includes(user?.role);
  const canIssue = ISSUE_ROLES.includes(user?.role) || user?.role === 'STOREKEEPER';

  const approveInspectionCost = async (inspId) => {
    setBusy(`approve-cost-${inspId}`);
    try {
      const r = await axios.post(`${API}/inspections/${inspId}/approve-cost`, {}, { withCredentials: true });
      toast.success(`Approved: ${Number(r.data.final_agreed_cost).toLocaleString()}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approve failed');
    } finally { setBusy(null); }
  };

  const rejectInspectionCost = async (inspId) => {
    const reason = window.prompt('Reason for rejection?', '');
    if (reason === null) return;
    setBusy(`reject-cost-${inspId}`);
    try {
      await axios.post(`${API}/inspections/${inspId}/reject-cost`, { reason }, { withCredentials: true });
      toast.success('Rejected');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reject failed');
    } finally { setBusy(null); }
  };

  const approvePo = async (pid) => {
    setBusy(`approve-po-${pid}`);
    try {
      await axios.post(`${API}/purchase-orders/${pid}/approve`, {}, { withCredentials: true });
      toast.success('LPO approved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); } finally { setBusy(null); }
  };

  const rejectPo = async (pid) => {
    const reason = window.prompt('Reason for rejection?', '');
    if (reason === null) return;
    setBusy(`reject-po-${pid}`);
    try {
      await axios.post(`${API}/purchase-orders/${pid}/reject`, { reason }, { withCredentials: true });
      toast.success('Rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); } finally { setBusy(null); }
  };

  const approveVoucher = async (vid) => {
    setBusy(`approve-v-${vid}`);
    try {
      await axios.post(`${API}/vouchers/${vid}/approve`, {}, { withCredentials: true });
      toast.success('Voucher approved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); } finally { setBusy(null); }
  };

  const rejectVoucher = async (vid) => {
    const reason = window.prompt('Reason for rejection?', '');
    if (reason === null) return;
    setBusy(`reject-v-${vid}`);
    try {
      await axios.post(`${API}/vouchers/${vid}/reject`, { reason }, { withCredentials: true });
      toast.success('Rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); } finally { setBusy(null); }
  };

  const approveNote = async (nid) => {
    setBusy(`approve-n-${nid}`);
    try {
      await axios.post(`${API}/credit-debit-notes/${nid}/approve`, {}, { withCredentials: true });
      toast.success('Note approved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); } finally { setBusy(null); }
  };
  const rejectNote = async (nid) => {
    const reason = window.prompt('Reason for rejection?', '');
    if (reason === null) return;
    setBusy(`reject-n-${nid}`);
    try {
      await axios.post(`${API}/credit-debit-notes/${nid}/reject`, { reason }, { withCredentials: true });
      toast.success('Rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); } finally { setBusy(null); }
  };

  const approveMR = async (mrid) => {
    setBusy(`approve-mr-${mrid}`);
    try {
      await axios.put(`${API}/material-requests/${mrid}/approve`, {}, { withCredentials: true });
      toast.success('Material request approved — ready to issue');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); } finally { setBusy(null); }
  };
  const rejectMR = async (mrid) => {
    const reason = window.prompt('Reason for rejection?');
    if (!reason) return;
    setBusy(`reject-mr-${mrid}`);
    try {
      await axios.put(`${API}/material-requests/${mrid}/reject`, { reason }, { withCredentials: true });
      toast.success('Rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); } finally { setBusy(null); }
  };

  const approvePR = async (prid) => {
    setBusy(`approve-pr-${prid}`);
    try {
      await axios.put(`${API}/purchase-requests/${prid}/approve`, {}, { withCredentials: true });
      toast.success('Purchase Request approved — ready for procurement');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); } finally { setBusy(null); }
  };
  const rejectPR = async (prid) => {
    const notes = window.prompt('Reason for rejection?');
    if (!notes) return;
    setBusy(`reject-pr-${prid}`);
    try {
      await axios.put(`${API}/purchase-requests/${prid}/reject`, { notes }, { withCredentials: true });
      toast.success('Rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); } finally { setBusy(null); }
  };

  const approveSR = async (srid) => {
    setBusy(`approve-sr-${srid}`);
    try {
      await axios.put(`${API}/service-reports/${srid}/approve`, {}, { withCredentials: true });
      toast.success('Service Report verified — complaint auto-closed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed — check the closure gates'); } finally { setBusy(null); }
  };
  const rejectSR = async (srid) => {
    const reason = window.prompt('Reason for rejection?');
    if (!reason) return;
    setBusy(`reject-sr-${srid}`);
    try {
      await axios.put(`${API}/service-reports/${srid}/reject`, { reason }, { withCredentials: true });
      toast.success('Rejected — sent back for rework');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); } finally { setBusy(null); }
  };

  // --- QUEUE 1: material requests awaiting engineer decision ---
  const pendingApproval = reports.filter(r =>
    (r.materials_needed || []).some(m => m.status === 'pending')
  );
  // --- QUEUE 2: approved but not yet issued (storekeeper action) ---
  const readyToIssue = reports.filter(r =>
    (r.materials_needed || []).some(m => m.status === 'approved')
  );
  // --- QUEUE 3: reports awaiting engineer sign-off (closure) ---
  const readyToSignoff = reports.filter(r => {
    if (['approved', 'rejected'].includes(r.status)) return false;
    const mats = r.materials_needed || [];
    const anyPending = mats.some(m => m.status === 'pending');
    const anyUnissued = mats.some(m => m.status === 'approved');
    const notReturned = mats.some(m => m.is_returnable && m.status === 'issued' && !m.returned_at);
    return !anyPending && !anyUnissued && !notReturned;
  });
  // --- QUEUE 4 (technician): my requests + their status ---
  const myRequests = user?.role === 'TECHNICIAN'
    ? reports.filter(r => r.technician_id === user.id && (r.materials_needed || []).length > 0)
    : [];

  if (loading) return <Layout><div data-testid="approvals-loading" className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div></Layout>;

  return (
    <Layout>
      <div data-testid="approvals-page" className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Check className="w-7 h-7 text-emerald-500" />Approvals
          </h1>
          <p className="text-sm text-muted-foreground">One place to review and act on everything waiting for your decision.</p>
        </div>

        {/* Feb-2026 — Tabbed layout so engineers can jump between Material
            Requests, Purchase Requests and Service Reports quickly. */}
        {canApprove && (
          <Tabs defaultValue="mr" className="w-full">
            <TabsList className="flex-wrap h-auto gap-1">
              <TabsTrigger value="mr" data-testid="tab-mr">
                <Package className="w-4 h-4 mr-1" />MRs
                {pendingMRs.length > 0 && <span className="ml-1.5 px-1.5 py-0 rounded-full bg-purple-600 text-white text-[10px]">{pendingMRs.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="pr" data-testid="tab-pr">
                <ShoppingCart className="w-4 h-4 mr-1" />PRs
                {pendingPRs.length > 0 && <span className="ml-1.5 px-1.5 py-0 rounded-full bg-emerald-600 text-white text-[10px]">{pendingPRs.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="sr" data-testid="tab-sr">
                <FileBarChart className="w-4 h-4 mr-1" />Service Reports
                {pendingSRs.length > 0 && <span className="ml-1.5 px-1.5 py-0 rounded-full bg-teal-600 text-white text-[10px]">{pendingSRs.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="po" data-testid="tab-po">
                <ClipboardCheck className="w-4 h-4 mr-1" />POs
                {pendingPOs.length > 0 && <span className="ml-1.5 px-1.5 py-0 rounded-full bg-sky-600 text-white text-[10px]">{pendingPOs.length}</span>}
              </TabsTrigger>
              <TabsTrigger value="other" data-testid="tab-other">More</TabsTrigger>
            </TabsList>

            <TabsContent value="mr" className="mt-3 space-y-3">
              {pendingMRs.length === 0 && <div className="p-8 text-center text-muted-foreground">No pending material requests 🎉</div>}
              {pendingMRs.map(mr => (
                <div key={mr.id} data-testid={`mr-approval-row-${mr.mr_number}`} className="p-3 border rounded-md bg-white dark:bg-slate-900 flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-semibold text-sm">{mr.mr_number}</span>
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">Pending</span>
                      {mr.work_order_number && <a href="/work-orders" className="text-[11px] text-teal-700 hover:underline">↳ {mr.work_order_number}</a>}
                      {mr.ticket_number && <a href={`/complaints?highlight=${mr.complaint_id || ''}`} className="text-[11px] text-orange-700 hover:underline">↳ {mr.ticket_number}</a>}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">Requested by <span className="font-medium">{mr.requested_by_name || mr.requested_by || '—'}</span></div>
                    {mr.reason && <div className="text-xs italic text-slate-500 mt-1">&ldquo;{mr.reason}&rdquo;</div>}
                    <ul className="text-xs mt-1 space-y-0.5">
                      {(mr.lines || []).map((l, i) => (
                        <li key={i} className="flex justify-between border-b border-slate-100 py-0.5">
                          <span>{l.item_name} — {l.qty} {l.unit || ''}{l.is_returnable ? ' 🔁 returnable' : ''}</span>
                          <span className="text-slate-400">{l.notes || ''}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`approve-mr-btn-${mr.id}`} disabled={busy === `approve-mr-${mr.id}`} onClick={() => approveMR(mr.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-4 h-4 mr-1" />Approve</Button>
                    <Button data-testid={`reject-mr-btn-${mr.id}`} disabled={busy === `reject-mr-${mr.id}`} onClick={() => rejectMR(mr.id)} variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50"><X className="w-4 h-4 mr-1" />Reject</Button>
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="pr" className="mt-3 space-y-3">
              {pendingPRs.length === 0 && <div className="p-8 text-center text-muted-foreground">No pending purchase requests 🎉</div>}
              {pendingPRs.map(pr => (
                <div key={pr.id} data-testid={`pr-approval-row-${pr.pr_number}`} className="p-3 border rounded-md bg-white dark:bg-slate-900 space-y-2">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-[260px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono font-semibold text-sm">{pr.pr_number}</span>
                        <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">Pending Eng. Approval</span>
                        {pr.linked_mr_number && <a href={`/material-requests?highlight=${pr.linked_mr_number}`} className="text-[11px] text-purple-700 hover:underline">↳ from {pr.linked_mr_number}</a>}
                        {pr.work_order_number && <a href="/work-orders" className="text-[11px] text-teal-700 hover:underline">↳ {pr.work_order_number}</a>}
                        {pr.ticket_number && <a href={`/complaints?highlight=${pr.complaint_id || ''}`} className="text-[11px] text-orange-700 hover:underline">↳ {pr.ticket_number}</a>}
                      </div>
                      {pr.notes && <div className="text-xs italic text-slate-500 mt-1">{pr.notes}</div>}
                    </div>
                    <div className="flex gap-2">
                      <Button data-testid={`view-pr-btn-${pr.id}`} size="sm" variant="ghost" title="Toggle inline complaint details" onClick={() => togglePrExpand(pr)}><Eye className="w-4 h-4" /></Button>
                      <Button data-testid={`open-pr-page-${pr.id}`} size="sm" variant="ghost" title="Open in Purchase Requests page (full context — chain, history, thread)" onClick={() => navigate(`/purchase-requests?focus=${pr.id}`)}><ExternalLink className="w-4 h-4" /></Button>
                      <Button data-testid={`approve-pr-btn-${pr.id}`} disabled={busy === `approve-pr-${pr.id}`} onClick={() => approvePR(pr.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-4 h-4 mr-1" />Approve</Button>
                      <Button data-testid={`reject-pr-btn-${pr.id}`} disabled={busy === `reject-pr-${pr.id}`} onClick={() => rejectPR(pr.id)} variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50"><X className="w-4 h-4 mr-1" />Reject</Button>
                    </div>
                  </div>
                  <ul className="text-xs space-y-0.5">
                    {(pr.items || []).map((it, i) => (
                      <li key={i} className="flex justify-between border-b border-slate-100 py-0.5">
                        <span>{it.item_name} — {it.quantity} {it.unit || ''}</span>
                        <span className="text-slate-400">unit @ {it.unit_cost || 0}</span>
                      </li>
                    ))}
                  </ul>
                  {expandedPr === pr.id && (
                    <div data-testid={`pr-details-${pr.id}`} className="border-t pt-2 mt-2 text-xs space-y-1 bg-slate-50 dark:bg-slate-800/40 -mx-3 -mb-3 px-3 pb-3 rounded-b">
                      <div><span className="text-slate-500">Priority:</span> {pr.priority || 'normal'}</div>
                      <div><span className="text-slate-500">Requested by:</span> {pr.requested_by_name || pr.requested_by || '—'}</div>
                      <div><span className="text-slate-500">Total estimate:</span> {(pr.total_estimate || pr.total || 0).toFixed?.(2) || 0}</div>
                      {pr.building_name && <div><span className="text-slate-500">Building:</span> {pr.building_name}</div>}

                      {/* Feb-2026 — inline linked-complaint context so engineer
                          can approve/reject without jumping to Complaints. */}
                      {pr.complaint_id && (() => {
                        const c = complaints.find(x => x.id === pr.complaint_id);
                        if (!c) return null;
                        return (
                          <div data-testid={`pr-complaint-inline-${pr.id}`} className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                            <div className="text-[10px] uppercase font-bold text-orange-700 mb-1 flex items-center gap-1">
                              <Wrench className="w-3 h-3" />Linked Complaint
                              {pr.ticket_number && <span className="font-mono">· {pr.ticket_number}</span>}
                            </div>
                            <div className="space-y-1 rounded border border-orange-200 bg-white dark:bg-slate-900 p-2">
                              <div className="font-semibold text-sm text-foreground">{c.title || '(untitled)'}</div>
                              <div className="flex flex-wrap gap-2 text-[10px]">
                                {c.priority && <span className={`px-1.5 py-0.5 rounded font-semibold uppercase ${c.priority === 'urgent' ? 'bg-rose-100 text-rose-800' : c.priority === 'high' ? 'bg-orange-100 text-orange-800' : c.priority === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>{c.priority}</span>}
                                {c.status && <span className="px-1.5 py-0.5 rounded font-semibold uppercase bg-blue-100 text-blue-800">{c.status.replace(/_/g, ' ')}</span>}
                                {c.category && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{c.category}</span>}
                              </div>
                              {c.description && <div className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{c.description.length > 240 ? c.description.slice(0, 240) + '…' : c.description}</div>}
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-600 dark:text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-700">
                                {c.reporter_name && <span>👤 {c.reporter_name}</span>}
                                {c.reporter_phone && <span>📞 {c.reporter_phone}</span>}
                                {c.reporter_email && <span>✉ {c.reporter_email}</span>}
                                {c.assigned_to_name && <span>🔧 Assigned: {c.assigned_to_name}</span>}
                              </div>
                              <a href={`/complaints?highlight=${pr.complaint_id}`} className="inline-block text-orange-700 hover:underline text-[11px] font-semibold mt-1">Open full complaint ↗</a>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))}
            </TabsContent>

            <TabsContent value="sr" className="mt-3 space-y-3">
              {pendingSRs.length === 0 && <div className="p-8 text-center text-muted-foreground">No service reports awaiting verification 🎉</div>}
              {pendingSRs.map(sr => (
                <div key={sr.id} data-testid={`sr-approval-row-${sr.id}`} className="p-3 border rounded-md bg-white dark:bg-slate-900 flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-semibold text-sm">SR-{sr.id.slice(0, 8)}</span>
                      <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 border border-teal-300">Completed</span>
                      {sr.work_order_number && <a href="/work-orders" className="text-[11px] text-teal-700 hover:underline">↳ {sr.work_order_number}</a>}
                      {sr.complaint_id && (
                        <a
                          href={`/complaints?highlight=${sr.complaint_id}`}
                          data-testid={`sr-open-complaint-${sr.id}`}
                          className="text-[11px] text-amber-700 hover:underline"
                          title="Open the complaint's Command Center"
                        >↳ Open Complaint ↗</a>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">By <span className="font-medium">{sr.technician_name || '—'}</span> · {sr.summary?.slice(0, 90) || 'no summary'}</div>
                    <div className="flex gap-3 text-[11px] text-slate-500 mt-1">
                      <span>📷 {(sr.photos || []).length} photo{(sr.photos || []).length === 1 ? '' : 's'}</span>
                      <span>✍️ {sr.tenant_signature || sr.tenant_signature_url ? 'Signed' : 'Not signed'}</span>
                      <span>📦 {(sr.materials_used || []).length} material line{(sr.materials_used || []).length === 1 ? '' : 's'}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`view-sr-btn-${sr.id}`} onClick={() => navigate(`/reports?focus=${sr.id}`)} size="sm" variant="ghost" title="Open full Service Report — see photos, materials, work-done details before deciding"><Eye className="w-4 h-4" /></Button>
                    <Button data-testid={`approve-sr-btn-${sr.id}`} disabled={busy === `approve-sr-${sr.id}`} onClick={() => approveSR(sr.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-4 h-4 mr-1" />Verify &amp; Close</Button>
                    <Button data-testid={`reject-sr-btn-${sr.id}`} disabled={busy === `reject-sr-${sr.id}`} onClick={() => rejectSR(sr.id)} variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50"><X className="w-4 h-4 mr-1" />Reject</Button>
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="po" className="mt-3 space-y-3" data-testid="tab-po-content">
              <div className="text-xs text-muted-foreground">Purchase Orders pending approval are shown in the individual sections below (kept for full detail).</div>
            </TabsContent>

            <TabsContent value="other" className="mt-3 space-y-3" data-testid="tab-other-content">
              <div className="text-xs text-muted-foreground">Vouchers, Credit/Debit notes, Returns, Inspection cost approvals continue below.</div>
            </TabsContent>
          </Tabs>
        )}

        {/* Legacy Material-Requests-embedded-on-Service-Reports (kept for backward compat) */}
        {false && canApprove && (
          <Section
            testId="section-pending-mrs"
            icon={<Package className="w-5 h-5 text-purple-600" />}
            title="Material requests from Work Orders — awaiting your approval"
            count={pendingMRs.length}
            empty="No pending material requests 🎉"
          >
            {pendingMRs.map(mr => (
              <div key={mr.id} data-testid={`mr-approval-row-${mr.mr_number}`} className="p-3 border rounded-md bg-white flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-sm">{mr.mr_number}</span>
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">Pending</span>
                    {mr.work_order_number && (
                      <a href="/work-orders" className="text-[11px] text-teal-700 hover:underline">↳ {mr.work_order_number}</a>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">Requested by <span className="font-medium">{mr.requested_by_name || mr.requested_by || '—'}</span></div>
                  {mr.reason && <div className="text-xs italic text-slate-500 mt-1">&ldquo;{mr.reason}&rdquo;</div>}
                  <ul className="text-xs mt-1 space-y-0.5">
                    {(mr.lines || []).map((l, i) => (
                      <li key={i} className="flex justify-between border-b border-slate-100 py-0.5">
                        <span>{l.item_name} — {l.qty} {l.unit || ''}{l.is_returnable ? ' 🔁 returnable' : ''}</span>
                        <span className="text-slate-400">{l.notes || ''}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`approve-mr-btn-${mr.id}`} disabled={busy === `approve-mr-${mr.id}`} onClick={() => approveMR(mr.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Check className="w-4 h-4 mr-1" />Approve
                  </Button>
                  <Button data-testid={`reject-mr-btn-${mr.id}`} disabled={busy === `reject-mr-${mr.id}`} onClick={() => rejectMR(mr.id)} variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50">
                    <X className="w-4 h-4 mr-1" />Reject
                  </Button>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Engineer / Admin — Purchase Requests awaiting engineer approval — moved into tabs above */}
        {false && canApprove && (
          <Section
            testId="section-pending-prs"
            icon={<ClipboardCheck className="w-5 h-5 text-emerald-600" />}
            title="Purchase Requests — awaiting engineer approval"
            count={pendingPRs.length}
            empty="No pending purchase requests 🎉"
          >
            {pendingPRs.map(pr => (
              <div key={pr.id} data-testid={`pr-approval-row-${pr.pr_number}`} className="p-3 border rounded-md bg-white flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2">
                    <span className="font-mono font-semibold text-sm">{pr.pr_number}</span>
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300">Pending Eng. Approval</span>
                    {pr.linked_mr_id && (
                      <a href="/material-requests" className="text-[11px] text-purple-700 hover:underline">↳ from MR</a>
                    )}
                  </div>
                  {pr.notes && <div className="text-xs italic text-slate-500 mt-1">{pr.notes}</div>}
                  <ul className="text-xs mt-1 space-y-0.5">
                    {(pr.items || []).map((it, i) => (
                      <li key={i} className="flex justify-between border-b border-slate-100 py-0.5">
                        <span>{it.item_name} — {it.quantity} {it.unit || ''}</span>
                        <span className="text-slate-400">unit @ {it.unit_cost || 0}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`approve-pr-btn-${pr.id}`} disabled={busy === `approve-pr-${pr.id}`} onClick={() => approvePR(pr.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Check className="w-4 h-4 mr-1" />Approve
                  </Button>
                  <Button data-testid={`reject-pr-btn-${pr.id}`} disabled={busy === `reject-pr-${pr.id}`} onClick={() => rejectPR(pr.id)} variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50">
                    <X className="w-4 h-4 mr-1" />Reject
                  </Button>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Engineer / Admin — Service Reports awaiting engineer verification — moved into tabs above */}
        {false && canApprove && (
          <Section
            testId="section-pending-srs"
            icon={<FileBarChart className="w-5 h-5 text-teal-600" />}
            title="Service Reports — awaiting your verification"
            count={pendingSRs.length}
            empty="No service reports awaiting verification 🎉"
          >
            {pendingSRs.map(sr => (
              <div key={sr.id} data-testid={`sr-approval-row-${sr.id}`} className="p-3 border rounded-md bg-white flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[260px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold text-sm">SR-{sr.id.slice(0, 8)}</span>
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 border border-teal-300">Completed</span>
                    {sr.work_order_number && (
                      <a href="/work-orders" className="text-[11px] text-teal-700 hover:underline">↳ {sr.work_order_number}</a>
                    )}
                    {sr.complaint_id && (
                      <span className="text-[11px] text-amber-700">↳ complaint linked</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-600 mt-1">By <span className="font-medium">{sr.technician_name || '—'}</span> · {sr.summary?.slice(0, 90) || 'no summary'}</div>
                  <div className="flex gap-3 text-[11px] text-slate-500 mt-1">
                    <span>📷 {(sr.photos || []).length} photo{(sr.photos || []).length === 1 ? '' : 's'}</span>
                    <span>✍️ {sr.tenant_signature || sr.tenant_signature_url ? 'Signed' : 'Not signed'}</span>
                    <span>📦 {(sr.materials_used || []).length} material line{(sr.materials_used || []).length === 1 ? '' : 's'}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button data-testid={`approve-sr-btn-${sr.id}`} disabled={busy === `approve-sr-${sr.id}`} onClick={() => approveSR(sr.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <Check className="w-4 h-4 mr-1" />Verify &amp; Close
                  </Button>
                  <Button data-testid={`reject-sr-btn-${sr.id}`} disabled={busy === `reject-sr-${sr.id}`} onClick={() => rejectSR(sr.id)} variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50">
                    <X className="w-4 h-4 mr-1" />Reject
                  </Button>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Engineer / Admin — material requests awaiting decision */}
        {canApprove && (
          <Section
            testId="section-pending-approval"
            icon={<Clock className="w-5 h-5" />}
            title="Material requests awaiting your decision"
            count={pendingApproval.length}
            empty="No pending material requests 🎉"
          >
            {pendingApproval.map(r => (
              <ReportRow key={r.id} r={r} complaints={complaints}
                actions={[
                  { label: 'Review & Approve', testId: `approve-mat-${r.id}`, icon: <Check className="w-4 h-4 mr-1" />, cls: 'bg-green-600 hover:bg-green-700', onClick: () => navigate(`/reports?focus=${r.id}`) },
                ]} />
            ))}
          </Section>
        )}

        {/* Feb-18 — Manager: inspection cost approvals (snagging / tenant check-out) */}
        {canApproveInspectionCost && (
          <Section
            testId="section-inspection-costs"
            icon={<ClipboardCheck className="w-5 h-5" />}
            title="Inspection cost approvals"
            count={pendingInspectionCosts.length}
            empty="No inspection costs pending approval."
          >
            {pendingInspectionCosts.map(insp => (
              <div key={insp.id} data-testid={`insp-cost-row-${insp.id}`} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-800/40 flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono text-muted-foreground">{insp.inspection_number} · {(insp.inspection_type || '').replace(/_/g, ' ')}</div>
                  <div className="font-medium">{insp.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {insp.building_name || '—'}{insp.unit_number ? ` · Unit ${insp.unit_number}` : ''} · Submitted by {insp.submitted_for_cost_approval_by_name || '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase text-amber-700">Pending Total</div>
                  <div className="text-lg font-bold font-mono">{insp.currency || 'AED'} {Number(insp.preview_total_cost || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground">{insp.total_snags || 0} snags · {insp.checklist_count || 0} checklist rows</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/inspections/${insp.id}`)}>Review</Button>
                  <Button data-testid={`insp-approve-${insp.id}`} size="sm" onClick={() => approveInspectionCost(insp.id)} disabled={busy === `approve-cost-${insp.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button data-testid={`insp-reject-${insp.id}`} size="sm" variant="outline" onClick={() => rejectInspectionCost(insp.id)} disabled={busy === `reject-cost-${insp.id}`} className="text-rose-700 border-rose-300">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Feb-18 — Manager: LPO / Purchase Order approvals */}
        {canApproveInspectionCost && (
          <Section
            testId="section-po-approvals"
            icon={<ClipboardCheck className="w-5 h-5" />}
            title="LPO / Purchase Order approvals"
            count={pendingPOs.length}
            empty="No purchase orders waiting for approval."
          >
            {pendingPOs.map(po => (
              <div key={po.id} data-testid={`po-approval-row-${po.id}`} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-800/40 flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono text-muted-foreground">{po.po_number}</div>
                  <div className="font-medium">{po.supplier_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {(po.line_items || []).length} line item(s) · Issued {po.issue_date || '—'} · Submitted by {po.submitted_for_approval_by_name || '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase text-amber-700">Total (incl. {po.tax_rate || 0}% tax)</div>
                  <div className="text-lg font-bold font-mono">{po.currency || 'AED'} {Number(po.total_amount || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground">Subtotal {Number(po.subtotal || 0).toLocaleString()} · Tax {Number(po.tax_amount || 0).toLocaleString()}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => navigate('/purchase-orders')}>Open</Button>
                  <Button data-testid={`po-approve-${po.id}`} size="sm" onClick={() => approvePo(po.id)} disabled={busy === `approve-po-${po.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button data-testid={`po-reject-${po.id}`} size="sm" variant="outline" onClick={() => rejectPo(po.id)} disabled={busy === `reject-po-${po.id}`} className="text-rose-700 border-rose-300">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Feb-18 — Manager: Voucher approvals (Payment / Receipt) */}
        {canApproveInspectionCost && (
          <Section
            testId="section-voucher-approvals"
            icon={<ClipboardCheck className="w-5 h-5" />}
            title="Voucher approvals (Payment / Receipt)"
            count={pendingVouchers.length}
            empty="No vouchers waiting for approval."
          >
            {pendingVouchers.map(v => (
              <div key={v.id} data-testid={`voucher-approval-row-${v.id}`} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-800/40 flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono text-muted-foreground">{v.voucher_no} · {v.voucher_type === 'payment' ? 'PAYMENT' : 'RECEIPT'}</div>
                  <div className="font-medium">{v.party_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {v.narration?.slice(0, 80) || '—'} · {v.date?.split('T')[0]} · Submitted by {v.submitted_for_approval_by_name || '—'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase text-amber-700">Amount</div>
                  <div className="text-lg font-bold font-mono">{v.currency || 'AED'} {Number(v.amount || 0).toLocaleString()}</div>
                  <div className="text-[10px] text-muted-foreground capitalize">{v.mode}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => navigate('/vouchers')}>Open</Button>
                  <Button data-testid={`v-approve-${v.id}`} size="sm" onClick={() => approveVoucher(v.id)} disabled={busy === `approve-v-${v.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button data-testid={`v-reject-${v.id}`} size="sm" variant="outline" onClick={() => rejectVoucher(v.id)} disabled={busy === `reject-v-${v.id}`} className="text-rose-700 border-rose-300">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Feb-18 — Manager: Credit / Debit Note approvals */}
        {canApproveInspectionCost && (
          <Section
            testId="section-cd-notes"
            icon={<ClipboardCheck className="w-5 h-5" />}
            title="Credit / Debit Note approvals"
            count={pendingNotes.length}
            empty="No credit or debit notes waiting for approval."
          >
            {pendingNotes.map(n => (
              <div key={n.id} data-testid={`note-approval-row-${n.id}`} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-white dark:bg-slate-800/40 flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono text-muted-foreground">
                    {n.note_number} · <span className={n.note_type === 'credit' ? 'text-emerald-700' : 'text-rose-700'}>{n.note_type.toUpperCase()} NOTE</span> · {n.party_type}
                  </div>
                  <div className="font-medium">{n.party_name}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">
                    {n.description}
                    {n.link_ref ? <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 font-mono">{n.link_ref}</span> : null}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] font-bold uppercase text-amber-700">Amount</div>
                  <div className="text-lg font-bold font-mono">{n.currency} {Number(n.amount).toLocaleString()}</div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => navigate('/credit-debit-notes')}>Open</Button>
                  <Button data-testid={`n-approve-${n.id}`} size="sm" onClick={() => approveNote(n.id)} disabled={busy === `approve-n-${n.id}`} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Check className="w-4 h-4 mr-1" /> Approve
                  </Button>
                  <Button data-testid={`n-reject-${n.id}`} size="sm" variant="outline" onClick={() => rejectNote(n.id)} disabled={busy === `reject-n-${n.id}`} className="text-rose-700 border-rose-300">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </Section>
        )}

        {/* Storekeeper (and admins) — approved materials ready to issue */}
        {canIssue && (
          <Section
            testId="section-ready-to-issue"
            icon={<Package className="w-5 h-5" />}
            title="Materials ready to issue"
            count={readyToIssue.length}
            empty="Nothing waiting to be issued from stock."
          >
            {readyToIssue.map(r => (
              <ReportRow key={r.id} r={r} complaints={complaints}
                actions={[
                  { label: 'Issue from stock', testId: `issue-from-approvals-${r.id}`, icon: <Package className="w-4 h-4 mr-1" />, cls: 'bg-blue-600 hover:bg-blue-700', onClick: () => doAction('issue-materials', r.id), busy: busy === `issue-materials-${r.id}` },
                ]} />
            ))}
          </Section>
        )}

        {/* Storekeeper — returnable items pending collection */}
        {(user?.role === 'STOREKEEPER' || canApprove) && (
          <Section
            testId="section-returnable"
            icon={<RotateCcw className="w-5 h-5" />}
            title="Returnable items pending collection"
            count={pendingReturns.length}
            empty="No returnable items outstanding."
          >
            {pendingReturns.slice(0, 20).map((r, i) => (
              <div key={i} data-testid={`pending-return-${i}`} className="flex items-center gap-3 p-3 bg-white rounded border">
                <div className="flex-1 text-sm">
                  <div><strong>{r.item || r.material_name}</strong> · {r.qty} {r.unit || 'pcs'}</div>
                  <div className="text-xs text-slate-500">Issued to {r.technician_name} · {r.issued_at ? new Date(r.issued_at).toLocaleDateString() : ''}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate('/pending-returns')} data-testid={`goto-return-${i}`}>
                  Mark returned →
                </Button>
              </div>
            ))}
          </Section>
        )}

        {/* Engineer / Admin — ready for closure sign-off */}
        {canApprove && (
          <Section
            testId="section-signoff"
            icon={<FileBarChart className="w-5 h-5" />}
            title="Service reports awaiting your sign-off"
            count={readyToSignoff.length}
            empty="No reports awaiting sign-off."
          >
            {readyToSignoff.map(r => (
              <ReportRow key={r.id} r={r} complaints={complaints}
                actions={[
                  { label: 'Review & Approve', testId: `approve-report-${r.id}`, icon: <Check className="w-4 h-4 mr-1" />, cls: 'bg-emerald-600 hover:bg-emerald-700', onClick: () => navigate(`/reports?focus=${r.id}`) },
                ]} />
            ))}
          </Section>
        )}

        {/* Technician — my requests */}
        {user?.role === 'TECHNICIAN' && (
          <Section
            testId="section-my-requests"
            icon={<Wrench className="w-5 h-5" />}
            title="My material requests — status"
            count={myRequests.length}
            empty="You haven't requested any materials yet."
          >
            {myRequests.map(r => (
              <ReportRow key={r.id} r={r} complaints={complaints} showMaterialStatus />
            ))}
          </Section>
        )}
      </div>
    </Layout>
  );
}

function Section({ testId, icon, title, count, empty, children }) {
  return (
    <Card data-testid={testId} className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-teal-600">{icon}</div>
        <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
        <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-teal-100 text-teal-800 font-semibold">{count}</span>
      </div>
      {count === 0 ? <p className="text-sm text-slate-500 italic">{empty}</p> : <div className="space-y-2">{children}</div>}
    </Card>
  );
}

function ReportRow({ r, complaints, actions = [], showMaterialStatus }) {
  const comp = complaints.find(c => c.id === r.complaint_id);
  const ticket = comp?.ticket_number || r.complaint_ticket_number;
  const pending = (r.materials_needed || []).filter(m => m.status === 'pending').length;
  const approved = (r.materials_needed || []).filter(m => m.status === 'approved').length;
  const issued = (r.materials_needed || []).filter(m => m.status === 'issued').length;

  return (
    <div data-testid={`report-row-${r.id}`} className="p-3 bg-white rounded border">
      <div className="flex flex-wrap items-center gap-2">
        {ticket && <code className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-amber-200 font-mono">{ticket}</code>}
        <span className="font-semibold text-sm truncate">{r.summary || comp?.title || 'Untitled'}</span>
        <span className="text-xs text-slate-500">by {r.technician_name}</span>
      </div>
      {(r.materials_needed || []).length > 0 && (
        <div className="mt-1 text-[12px] text-slate-600 flex flex-wrap gap-3">
          <span>📦 {r.materials_needed.length} item(s)</span>
          {pending > 0 && <span className="text-amber-700">⏳ {pending} pending</span>}
          {approved > 0 && <span className="text-lime-700">✓ {approved} approved</span>}
          {issued > 0 && <span className="text-blue-700">📤 {issued} issued</span>}
        </div>
      )}
      {showMaterialStatus && (
        <div className="mt-2 space-y-1">
          {r.materials_needed.map((m, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <code className="text-[10px] px-1 rounded bg-slate-200">MRQ-{ticket || '?'}-{String(i + 1).padStart(2, '0')}</code>
              <span>{m.item} × {m.qty} {m.unit}</span>
              <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide">{m.status}</span>
            </div>
          ))}
        </div>
      )}
      {actions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {actions.map((a, i) => (
            <Button key={i} data-testid={a.testId} onClick={a.onClick} size="sm" className={a.cls} disabled={a.busy}>
              {a.busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : a.icon}
              {a.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
